import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";

const SECTIONS = ["home", "about", "teams", "services", "news"];

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");

  // Scroll to section on click with offset for fixed navbar
  const scrollToSection = (id) => {
    const section = document.getElementById(id);
    if (section) {
      const yOffset = -80; // navbar height (px)
      const y = section.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
      setActiveSection(id); // highlight immediately on click
      setIsOpen(false);
    }
  };

  // Track which section is closest to viewport center
  useEffect(() => {
    const handleScroll = () => {
      const offset = 80; // navbar height offset
      const viewportCenter = window.innerHeight / 2 + offset;
      let minDistance = Infinity;
      let currentSection = SECTIONS[0];
      for (let i = 0; i < SECTIONS.length; i++) {
        const section = document.getElementById(SECTIONS[i]);
        if (section) {
          const rect = section.getBoundingClientRect();
          const sectionCenter = (rect.top + rect.bottom) / 2;
          const distance = Math.abs(sectionCenter - viewportCenter);
          if (distance < minDistance) {
            minDistance = distance;
            currentSection = SECTIONS[i];
          }
        }
      }
      setActiveSection(currentSection);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // run on mount
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="fixed top-0 w-full h-[12vh] bg-white shadow-md flex items-center justify-between px-6 lg:px-32 z-50">
      {/* Logo */}
      <img
        src={require("./SSPC.jpg")}
        alt="Company Logo"
        className="h-[12vh] w-[12vh] object-contain"
      />

      {/* Desktop Nav */}
      <ol className="hidden md:flex gap-6 lg:gap-8 items-center text-lg">
        {SECTIONS.map((item, index) => (
          <li
            key={index}
            className={`font-bold cursor-pointer capitalize ${
              activeSection === item ? "text-red-500" : ""
            }`}
            onClick={() => scrollToSection(item)}
          >
            {item.replace(/^[a-z]/, (c) => c.toUpperCase())}
          </li>
        ))}
      </ol>

      {/* Desktop Button */}
      <button
        className="hidden md:block border rounded-l-full rounded-br-full px-6 py-3 bg-red-500 text-white font-bold relative overflow-hidden 
          before:content-[''] before:absolute before:top-0 before:left-0 before:w-0 before:h-full 
          before:bg-black before:transition-all before:duration-500 before:ease-in-out 
          hover:before:w-full hover:text-white"
        onClick={() => scrollToSection("appointment")}
      >
        <span className="relative z-10">Appointment</span>
      </button>

      {/* Mobile Menu Icon */}
      <div className="md:hidden z-50">
        <button onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile Menu (Responsive for < md only) */}
      <div
        className={`absolute top-[12vh] left-0 w-full bg-white shadow-md flex flex-col items-center space-y-5 py-6 transition-all duration-300 ease-in-out md:hidden ${
          isOpen ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
      >
        {[...SECTIONS, "appointment"].map((item, index) => (
          <div
            key={index}
            className={`font-bold text-lg capitalize cursor-pointer ${
              activeSection === item ? "text-red-500" : ""
            }`}
            onClick={() => scrollToSection(item)}
          >
            {item.replace(/^[a-z]/, (c) => c.toUpperCase())}
          </div>
        ))}
      </div>
    </div>
  );
}
